import { MultiSelectOption } from '@dispatch/shared/types';
import {
  Autocomplete,
  AutocompleteOwnerState,
  AutocompleteRenderGetTagProps,
  AutocompleteRenderInputParams,
  Box,
  Button,
  ButtonGroup,
  Popper,
  PopperProps,
} from '@mui/material';
import React from 'react';
import {
  Controller,
  Control,
  FieldValues,
  FieldErrors,
  FieldError,
  UseFormRegister,
} from 'react-hook-form';
import { AutocompleteTextField } from './AutocompleteTextField';
export interface TextFieldProps {
  id: string;
  name?: string;
  defaultValue?: string | number;
  required?: boolean;
  control: Control<FieldValues, unknown>;
  errors?: FieldErrors;
  label?: string;
  options: MultiSelectOption[];
  value?: string | number;
  register?: UseFormRegister<FieldValues>;
  error?: FieldError;
  multiple?: boolean;
  onValueChange?: (
    value: MultiSelectOption | (string | number)[] | null
  ) => void;
  overrideOptionsLabel?: (option: MultiSelectOption) => string;
  allowSelectAll?: boolean;
  addScroll?: boolean;
  limitTags?: number;
  readOnly?: boolean;
  disabled?: boolean;
  disableClearable?: boolean;
  isOptionEqualToValue?: (
    option: MultiSelectOption,
    selectValue: unknown
  ) => boolean;
  // allows to optionally send in a custom tag renderer
  renderTags?:
    | ((
        value: MultiSelectOption[],
        getTagProps: AutocompleteRenderGetTagProps,
        ownerState: AutocompleteOwnerState<
          MultiSelectOption,
          boolean,
          boolean,
          undefined,
          'div'
        >
      ) => React.ReactNode)
    | undefined;
  autoComplete?: 'on' | 'new-password' | 'off';
}

export const SelectField = ({
  id,
  name,
  defaultValue,
  required = false,
  label,
  control,
  errors,
  error,
  options,
  onValueChange,
  multiple = false,
  overrideOptionsLabel,
  allowSelectAll = false,
  addScroll = false,
  limitTags = 20,
  readOnly = false,
  disabled = false,
  disableClearable = false,
  isOptionEqualToValue = (option: MultiSelectOption, selectValue: unknown) => {
    return option.value === selectValue;
  },
  renderTags,
  autoComplete,
}: TextFieldProps) => {
  const [open, setOpen] = React.useState<boolean>(false);
  const defaultProps = {
    options: [...options],
    getOptionLabel: (option: MultiSelectOption) => option.title,
  };
  const fieldName = name || id;

  const handleMultiChange = (
    onChange: (...event: unknown[]) => void,
    data: MultiSelectOption[]
  ) => {
    onChange(parseData(data));
    onValueChange && onValueChange(parseData(data));
  };

  const parseData = (data: MultiSelectOption[]): (string | number)[] => {
    return data.map((item: MultiSelectOption) => {
      if (typeof item === 'string' || typeof item === 'number') return item;
      return item.value;
    });
  };

  const getOptionLabel = (option: unknown) => {
    const multiSelectOption = option as MultiSelectOption;

    if (overrideOptionsLabel) {
      return overrideOptionsLabel(multiSelectOption);
    }

    if (multiSelectOption?.title) {
      return multiSelectOption.title;
    } else {
      const result = findOption(option);
      if (result) {
        return result.title;
      }
    }
    return '';
  };

  const findOption = (option: unknown) => {
    return options.find(({ value: findValue }) => findValue === option);
  };

  const closePopper = () => setOpen(false);
  const openPopper = () => setOpen(true);

  const handleHeight = multiple ? '2.75rem' : '';

  const handleSelectAll = (
    onChange: (...event: unknown[]) => void,
    selectedOptions: MultiSelectOption[]
  ) => {
    handleMultiChange(onChange, selectedOptions);
  };

  const renderTextField = (params: AutocompleteRenderInputParams) => {
    return (
      <AutocompleteTextField
        params={params}
        autoComplete={autoComplete}
        label={label}
        required={required}
        sx={{
          '.MuiAutocomplete-inputRoot': {
            minHeight: handleHeight,
            width: '100%',
          },
        }}
        error={error ? !!error : !!errors && !!errors[id]?.message}
        helperText={error ? error : !!errors && errors[id]?.message}
      />
    );
  };

  // controller is built in to react-hook-form and it allows your input to be registered with useForms internal state.
  return (
    <Controller
      data-testid="select-component"
      control={control}
      name={fieldName}
      defaultValue={defaultValue}
      render={({ field: { onChange, value } }) => {
        return (
          <>
            <Autocomplete
              {...defaultProps}
              readOnly={readOnly}
              disabled={disabled}
              open={open}
              disablePortal={true}
              disableClearable={disableClearable}
              onOpen={openPopper}
              onClose={closePopper}
              disableCloseOnSelect={multiple}
              limitTags={limitTags}
              ListboxProps={{ style: { maxHeight: '15rem' } }}
              multiple={multiple}
              getOptionLabel={getOptionLabel}
              value={value || (multiple ? [] : null)}
              renderInput={(params) => renderTextField(params)}
              PopperComponent={(props: PopperProps) => (
                <SelectPopper
                  {...props}
                  handleSelectAll={() => handleSelectAll(onChange, options)}
                  allowSelectAll={allowSelectAll}
                />
              )}
              isOptionEqualToValue={isOptionEqualToValue}
              renderTags={renderTags || undefined}
              onChange={(
                _,
                data: MultiSelectOption | MultiSelectOption[] | null
              ) => {
                if (multiple) {
                  if (!data) {
                    onValueChange?.([]);
                    return onChange('');
                  }
                  handleMultiChange(onChange, data as MultiSelectOption[]);
                } else {
                  if (!data) {
                    onValueChange?.(null);
                    return onChange('');
                  }

                  const d = data as MultiSelectOption;
                  onChange(d.value);
                  onValueChange && onValueChange(d);
                }
              }}
              renderOption={(
                props: React.HTMLAttributes<HTMLLIElement>,
                option: { title: string; value: string | number }
              ) => {
                return (
                  <Box
                    component="li"
                    {...props}
                    key={option.value}
                    sx={{ maxHeight: '100px' }}
                  >
                    {option.title}
                  </Box>
                );
              }}
            />
            {value?.length > 20 && open && addScroll ? (
              <div style={{ height: '31.25rem' }}></div>
            ) : null}
          </>
        );
      }}
    />
  );
};

type SelectPopperProps = {
  allowSelectAll: boolean;
  handleSelectAll: () => void;
} & PopperProps;

const SelectPopper = function ({
  allowSelectAll,
  handleSelectAll,
  ...rest
}: SelectPopperProps) {
  return (
    <Popper {...rest}>
      {allowSelectAll ? (
        <div
          style={{ display: 'flex', width: '100%', justifyContent: 'flex-end' }}
        >
          <ButtonGroup
            color="primary"
            aria-label="outlined primary button group"
          >
            <Button
              color="primary"
              variant="text"
              onClick={() => handleSelectAll?.()}
              onMouseDown={(e) => e.preventDefault()}
            >
              Select All
            </Button>
          </ButtonGroup>
        </div>
      ) : null}
      {rest.children}
    </Popper>
  );
};
